// A program and its first session(s) in one save — the 420 px SidePanel
// (board 25 v2 shape--5; field names ruling W6 from board 22's captions).
// Session kind: weekday chips (Saturday first), a time, «المدة بالدقائق»
// (default ٦٠) and «المقاعد»; «يتكرّر أسبوعياً» on = one POST per chosen
// weekday (R17), off («جلسة واحدة») = one date and one POST. Course kind
// («الدورة كاملة»): a start date, a time and «عدد الجلسات» → one POST with
// weeks. A sessions failure after the program saved keeps the panel on the
// sessions with the server's sentence; the retry posts only what is missing.

import { useState, type ReactNode } from 'react';
import { SidePanel } from '@/app/SidePanel';
import { PrimaryAction } from '@/components/Button';
import { Chip, Toggle } from '@/components/Form';
import { isApiError } from '@/lib/api';
import { normalizeDigits } from '@/lib/digits';
import { Msg } from '../../components/widgets';
import { ERRORS, PROGRAMS } from '../../copy';
import { baghdadAt, nextWeekdayAt } from '../../data/sessionTime';
import { createProgram, createSessions, type BookingKind, type Program, type ProgramBody, type SessionsBody } from '../../data/shapeApi';
import { WEEK } from '../../state';

interface Form {
  name: string;
  trainer: string;
  price: string;
  kind: BookingKind;
  meeting: string;
  weekly: boolean;
  days: number[];
  date: string;
  time: string;
  duration: string;
  seats: string;
  count: string;
}

const EMPTY: Form = { name: '', trainer: '', price: '', kind: 'session', meeting: '', weekly: true, days: [], date: '', time: '', duration: '60', seats: '', count: '' };
const num = (v: string): number | undefined => (v.trim() === '' ? undefined : Number(normalizeDigits(v)));

function messageOf(e: unknown): string {
  if (isApiError(e)) return e.errors?.find((r) => r.message)?.message ?? e.detail ?? (e.status >= 500 ? ERRORS.server : e.message);
  return ERRORS.network;
}

/** The session POSTs the form asks for, keyed so a retry skips the ones already made. */
function sessionPlan(f: Form, now = new Date()): Array<{ key: string; body: SessionsBody }> {
  const common = { duration_min: num(f.duration) ?? 60, seats: num(f.seats) ?? 0 };
  if (f.kind === 'course') return [{ key: 'course', body: { starts_at: baghdadAt(f.date, f.time), ...common, repeat_weekly: true, weeks: num(f.count) ?? 0 } }];
  if (!f.weekly) return [{ key: 'once', body: { starts_at: baghdadAt(f.date, f.time), ...common, repeat_weekly: false } }];
  return WEEK.filter((d) => f.days.includes(d.weekday)).map((d) => ({ key: `w${d.weekday}`, body: { starts_at: nextWeekdayAt(d.weekday, f.time, now), ...common, repeat_weekly: true } }));
}

export interface ProgramPanelProps {
  businessId: string;
  onClose: () => void;
  /** The program (and its sessions) changed: refresh the list. */
  onSaved: () => void;
}

export function ProgramPanel({ businessId, onClose, onSaved }: ProgramPanelProps) {
  const [form, setForm] = useState<Form>(EMPTY);
  const [program, setProgram] = useState<Program | null>(null);
  const [posted, setPosted] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const set = (patch: Partial<Form>) => {
    setForm((f) => ({ ...f, ...patch }));
    setErrors({});
  };

  const save = async () => {
    if (busy) return;
    const e: Record<string, string> = {};
    if (!form.time.trim()) e.sessions = 'موعد الجلسة مطلوب';
    if (form.kind === 'session' && form.weekly && form.days.length === 0) e.sessions = 'موعد الجلسة مطلوب';
    if ((form.kind === 'course' || !form.weekly) && !form.date) e.sessions = 'موعد الجلسة مطلوب';
    // booking's session rules (its sentences) before the program exists, so a bad session never strands a program
    const seats = num(form.seats);
    const duration = num(form.duration);
    if (!e.sessions && !(seats != null && seats >= 1 && seats <= 500)) e.sessions = 'عدد المقاعد غير صالح';
    if (!e.sessions && !(duration != null && duration >= 5 && duration <= 1440)) e.sessions = 'مدة الجلسة بين ٥ دقائق و٢٤ ساعة';
    if (!e.sessions && form.kind === 'course') {
      const weeks = num(form.count);
      if (!(weeks != null && weeks >= 1 && weeks <= 52)) e.sessions = 'عدد الأسابيع بين ١ و٥٢';
    }
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setBusy(true);
    setErrors({});
    try {
      let p = program;
      if (!p) {
        const body: ProgramBody = { name: form.name.trim(), seat_price_iqd: num(form.price) ?? 0, booking_kind: form.kind };
        if (form.trainer.trim()) body.trainer = form.trainer.trim();
        if (form.meeting.trim()) body.meeting_point = form.meeting.trim();
        if (num(form.price) === undefined) delete (body as Partial<ProgramBody>).seat_price_iqd;
        try {
          p = await createProgram(businessId, body);
          setProgram(p);
          onSaved();
        } catch (err) {
          if (isApiError(err) && err.status === 422 && err.errors?.length) {
            const out: Record<string, string> = {};
            for (const r of err.errors) if (r.field && !out[r.field]) out[r.field] = r.message ?? '';
            setErrors(out);
          } else setErrors({ general: messageOf(err) });
          return;
        }
      }
      const done = [...posted];
      for (const s of sessionPlan(form)) {
        if (done.includes(s.key)) continue;
        try {
          await createSessions(businessId, p.id, s.body);
          done.push(s.key);
          setPosted([...done]);
        } catch (err) {
          setErrors({ sessions: messageOf(err) });
          onSaved();
          return;
        }
      }
      onSaved();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const err = (k: string) => (errors[k] ? <Msg testId={`onb-pg-err-${k}`}>{errors[k]}</Msg> : null);
  const field = (label: ReactNode, control: ReactNode, k?: string) => (
    <div className="onb-ut-field">
      <span className="onb-ut-label">{label}</span>
      {control}
      {k && err(k)}
    </div>
  );
  const input = (testId: string, value: string, onChange: (v: string) => void, label: string, kind: 'text' | 'num' | 'time' | 'date' = 'text', disabled = false) => (
    <input
      className={kind === 'text' ? 'onb-entity-name' : 'onb-entity-price ltr'}
      dir={kind === 'text' ? undefined : 'ltr'}
      type={kind === 'time' || kind === 'date' ? kind : 'text'}
      inputMode={kind === 'num' ? 'numeric' : undefined}
      aria-label={label}
      data-testid={testId}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(kind === 'num' ? normalizeDigits(e.target.value) : e.target.value)}
    />
  );
  const locked = !!program; // the program is saved: only its sessions are left

  return (
    <SidePanel
      open
      title={program?.name || PROGRAMS.add}
      onClose={onClose}
      data-testid="onb-pg-panel"
      footer={
        <PrimaryAction size="lg" block loading={busy} onClick={() => void save()}>
          {PROGRAMS.save}
        </PrimaryAction>
      }
    >
      <div className="onb-ut-form">
        {err('general')}
        {field(PROGRAMS.name, input('onb-pg-name', form.name, (v) => set({ name: v }), PROGRAMS.name, 'text', locked), 'name')}
        {field(PROGRAMS.trainer, input('onb-pg-trainer', form.trainer, (v) => set({ trainer: v }), PROGRAMS.trainer, 'text', locked), 'trainer')}
        {field(PROGRAMS.seatPrice, input('onb-pg-price', form.price, (v) => set({ price: v }), PROGRAMS.seatPrice, 'num', locked), 'seat_price_iqd')}
        {field(
          PROGRAMS.kind,
          <div className="onb-sitting-opts">
            <Chip selected={form.kind === 'session'} disabled={locked} onClick={() => set({ kind: 'session' })}>
              {PROGRAMS.kindSession}
            </Chip>
            <Chip selected={form.kind === 'course'} disabled={locked} onClick={() => set({ kind: 'course' })}>
              {PROGRAMS.kindCourse}
            </Chip>
          </div>,
          'booking_kind',
        )}
        {field(PROGRAMS.meetingPoint, input('onb-pg-meeting', form.meeting, (v) => set({ meeting: v }), PROGRAMS.meetingPoint, 'text', locked), 'meeting_point')}

        <div className="onb-entity-section" data-testid="onb-pg-first">
          <div className="onb-entity-section-title">{PROGRAMS.first}</div>
          {form.kind === 'session' && <Toggle checked={form.weekly} onChange={(weekly) => set({ weekly })} label={PROGRAMS.weekly} description={form.weekly ? undefined : PROGRAMS.once} />}
          {form.kind === 'session' && form.weekly ? (
            <div className="onb-sitting-opts" role="group" aria-label={PROGRAMS.weekly}>
              {WEEK.map((d) => (
                <Chip key={d.weekday} selected={form.days.includes(d.weekday)} onClick={() => set({ days: form.days.includes(d.weekday) ? form.days.filter((x) => x !== d.weekday) : [...form.days, d.weekday] })}>
                  {d.name}
                </Chip>
              ))}
            </div>
          ) : (
            field(PROGRAMS.date, input('onb-pg-date', form.date, (v) => set({ date: v }), PROGRAMS.date, 'date'))
          )}
          {field(PROGRAMS.time, input('onb-pg-time', form.time, (v) => set({ time: v }), PROGRAMS.time, 'time'))}
          {field(PROGRAMS.duration, input('onb-pg-duration', form.duration, (v) => set({ duration: v }), PROGRAMS.duration, 'num'))}
          {field(PROGRAMS.seats, input('onb-pg-seats', form.seats, (v) => set({ seats: v }), PROGRAMS.seats, 'num'))}
          {form.kind === 'course' && field(PROGRAMS.sessionsCount, input('onb-pg-count', form.count, (v) => set({ count: v }), PROGRAMS.sessionsCount, 'num'))}
          {err('sessions')}
        </div>
      </div>
    </SidePanel>
  );
}
